import { useRef, useState, type FormEvent } from 'react'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { ApiError, api, apiErrorMessage } from '../../lib/api'
import { useI18n } from '../../lib/i18n'
import { ContactMessageForm } from './contact-message-form'
import './pilot-request-form.css'

export function PilotRequestForm() {
  return <div className="pilot-request-stack"><AssistedSetupForm /><ContactMessageForm /></div>
}

function AssistedSetupForm() {
  const { locale } = useI18n()
  const ar = locale === 'ar'
  const [method, setMethod] = useState('email')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [reference, setReference] = useState('')
  const attempt = useRef<{ payload: string; key: string } | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    const data = new FormData(event.currentTarget)
    const input = { businessName: data.get('businessName'), city: data.get('city'), businessType: data.get('businessType'),
      contactMethod: method, contactValue: data.get('contactValue'), locale: ar ? 'ar' : 'en',
      consent: data.get('consent') === 'on', website: data.get('website') }
    const payload = JSON.stringify(input)
    // Keep one key across uncertain network responses; editing starts a new request.
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: crypto.randomUUID() }
    setPending(true)
    setError('')
    try {
      const result = await api<{ reference: string }>('/api/pilot-requests/', {
        method: 'POST', body: JSON.stringify({ ...input, requestKey: attempt.current.key }),
      })
      setReference(result.reference)
    } catch (failure) {
      setError(failure instanceof ApiError && failure.status === 400
        ? (ar ? 'راجع البيانات، وأدخل بريدًا صحيحًا أو رقم جوال سعودي يبدأ بـ 05 أو +9665.' : 'Check your details and enter a valid email or Saudi mobile number starting with 05 or +9665.')
        : apiErrorMessage(failure, locale))
    } finally { setPending(false) }
  }

  if (reference) return <div className="pilot-request pilot-request--received" role="status" aria-live="polite">
    <CheckCircle2 size={28} aria-hidden="true" />
    <h3>{ar ? 'وصل طلبك' : 'Your request is saved'}</h3>
    <p>{ar ? 'سيراجع فريق سِعَة طلبك لترتيب الإعداد معك. لم تُنشأ مساحة عمل بعد، ولم تُرسل رسالة تأكيد تلقائية.' : 'The Seaa team will review your request to arrange setup with you. A workspace has not been created and no automatic confirmation message has been sent.'}</p>
    <p>{ar ? 'احتفظ برقم الطلب للمتابعة:' : 'Keep this reference for follow-up:'}<br /><strong className="pilot-request__reference" dir="ltr">{reference}</strong></p>
  </div>

  return <form className="pilot-request" onSubmit={(event) => void submit(event)} aria-label={ar ? 'طلب إعداد المنشأة' : 'Request business setup'}>
    <h3>{ar ? 'لنجهّز صفحة حجزك' : 'Let’s set up your booking page'}</h3>
    <p>{ar ? 'أدخل بيانات منشأتك الأساسية. سنراجع الطلب معك قبل إعداد المنشأة ونشر صفحتها.' : 'Share the basics. We will review the request with you before configuring and publishing your business.'}</p>
    <fieldset disabled={pending}>
      <label>{ar ? 'اسم المنشأة' : 'Business name'}<input name="businessName" autoComplete="organization" minLength={2} maxLength={160} required dir="auto" /></label>
      <div className="pilot-request__pair">
        <label>{ar ? 'المدينة' : 'City'}<input name="city" autoComplete="address-level2" minLength={2} maxLength={100} required dir="auto" /></label>
        <label>{ar ? 'نوع النشاط' : 'Business type'}<input name="businessType" minLength={2} maxLength={100} required placeholder={ar ? 'مثل ملعب أو استوديو' : 'e.g. courts or a studio'} dir="auto" /></label>
      </div>
      <label>{ar ? 'طريقة التواصل المفضلة' : 'Preferred contact method'}<select value={method} onChange={(event) => setMethod(event.target.value)}>
        <option value="email">{ar ? 'البريد الإلكتروني' : 'Email'}</option><option value="whatsapp">{ar ? 'واتساب' : 'WhatsApp'}</option><option value="phone">{ar ? 'اتصال هاتفي' : 'Phone call'}</option>
      </select></label>
      <label>{method === 'email' ? (ar ? 'البريد الإلكتروني' : 'Email address') : (ar ? 'رقم الجوال السعودي' : 'Saudi mobile number')}
        <input key={method === 'email' ? 'email' : 'phone'} name="contactValue" type={method === 'email' ? 'email' : 'tel'} autoComplete={method === 'email' ? 'email' : 'tel'} maxLength={254} required dir="ltr" placeholder={method === 'email' ? 'you@business.sa' : '05XXXXXXXX'} />
      </label>
      <label className="pilot-request__trap" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
      <label className="pilot-request__consent"><input name="consent" type="checkbox" required /><span>{ar ? 'أطلب التواصل معي لتجهيز تجربة المنشأة. تُستخدم هذه البيانات لمتابعة الطلب فقط، وتُحذف من سجل الطلبات بعد 90 يومًا.' : 'Please contact me about setting up a business pilot. These details are used only to follow up on this request and are removed from the request register after 90 days.'} <a href={`/privacy?lang=${locale}`}>{ar ? 'الخصوصية' : 'Privacy'}</a></span></label>
      <button className="marketing-button marketing-button--primary" type="submit">{pending ? (ar ? 'جارٍ حفظ الطلب…' : 'Saving request…') : (ar ? 'إرسال طلب الإعداد' : 'Request assisted setup')}<ArrowRight size={17} aria-hidden="true" /></button>
    </fieldset>
    {error && <p className="pilot-request__error" role="alert">{error}</p>}
  </form>
}
